import type {
  PublicResearchHeadToHeadResponse,
  WorldcupResearchFacetItem,
  WorldcupResearchQuery,
  WorldcupResearchTeamMatchStatRow,
} from '~/features/worldcup-research/types'
import type { ImplementedLocale } from '~/lib/i18n/shared'
import { Button, Card } from '@heroui/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Download, ShieldCheck } from 'lucide-react'
import * as React from 'react'
import { useLocale, useTranslations } from 'use-intl'
import { exportPublicResearchHeadToHeadCsv } from '~/features/public-research/api'
import {
  initialPublicResearchFacetFilters,
  initialPublicResearchFilters,
  publicResearchFacetsQueryOptions,
  publicResearchHeadToHeadQueryOptions,
} from '~/features/public-research/query-options'
import { resolveTeamLabelFromSlug, teamNameToSlug } from '~/features/public-research/team-slugs'
import { formatCompetitionName } from '~/lib/i18n/messages/domain/competitions'
import { formatTeamName } from '~/lib/i18n/messages/domain/teams'
import {
  downloadCsv,
  EvidenceList,
  formatProviderName,
  formatValue,
  isUnauthorizedResearchError,
  matchDetailPath,
  metricShare,
  PageHeader,
  readableTeamValue,
  ResearchStateNotice,
  SelectInput,
  SourceWarningList,
  SummaryMetric,
  uniqueValues,
  userVisibleWarnings,
} from './components'

export type HeadToHeadTab = 'direct' | 'common-opponents'

interface HeadToHeadResearchViewProps {
  initialTeam?: string
  initialOpponent?: string
  initialTab?: HeadToHeadTab
  onTabChange?: (tab: HeadToHeadTab) => void
}

function formatTeamOptionLabel(value: string, locale: ImplementedLocale, facets: WorldcupResearchFacetItem[] = []) {
  const resolved = resolveTeamLabelFromSlug(value, facets)
  return formatTeamName(resolved === value ? readableTeamValue(value) : resolved, locale)
}

function uniqueTeamOptions(values: Array<string | null | undefined>, locale: ImplementedLocale, facets: WorldcupResearchFacetItem[] = []) {
  return uniqueValues(values).sort((a, b) => teamNameToSlug(a).localeCompare(teamNameToSlug(b))).map(value => ({
    value,
    label: formatTeamOptionLabel(value, locale, facets),
  }))
}

function sourceProviderLabels(rows: Array<WorldcupResearchTeamMatchStatRow | null | undefined>) {
  return uniqueValues(rows.flatMap(row => row?.sourceReferences?.map((source: any) => source.provider) ?? [])).map(formatProviderName)
}

function preferredHeadToHeadTab(data: PublicResearchHeadToHeadResponse): HeadToHeadTab {
  if (data.matches.length > 0) {
    return 'direct'
  }
  return data.commonOpponents.length > 0 ? 'common-opponents' : 'direct'
}

export function HeadToHeadResearchView({
  initialTeam = 'argentina',
  initialOpponent = 'france',
  initialTab,
  onTabChange,
}: HeadToHeadResearchViewProps) {
  const t = useTranslations('ResearchWorkspace.headToHead')
  const locale = useLocale() as ImplementedLocale
  const facetsQuery = useQuery(publicResearchFacetsQueryOptions(initialPublicResearchFacetFilters))
  const [team, setTeam] = React.useState(readableTeamValue(initialTeam))
  const [opponent, setOpponent] = React.useState(readableTeamValue(initialOpponent))
  const [selectedTab, setSelectedTab] = React.useState<HeadToHeadTab | null>(initialTab ?? null)
  const [filters, setFilters] = React.useState<WorldcupResearchQuery>({
    ...initialPublicResearchFilters,
    limit: 100,
  })
  const query = useQuery(publicResearchHeadToHeadQueryOptions({
    ...filters,
    team,
    opponent,
  }))
  const exportMutation = useMutation({
    mutationFn: () => exportPublicResearchHeadToHeadCsv({ ...filters, team, opponent }),
    onSuccess: csv => downloadCsv(`national-team-head-to-head-${teamNameToSlug(team)}-${teamNameToSlug(opponent)}.csv`, csv),
  })
  const teams = facetsQuery.data?.facets.teams ?? []
  const opponents = facetsQuery.data?.facets.opponents ?? []
  const teamFacets = React.useMemo(() => [...teams, ...opponents], [teams, opponents])
  const teamOptions = uniqueTeamOptions([
    team,
    opponent,
    ...teamFacets.map(item => item.value),
  ], locale, teamFacets)
  const researchViewOptions = [
    { value: 'major_tournament', label: t('researchViewOptions.majorTournament') },
    { value: 'competitive_context', label: t('researchViewOptions.competitiveContext') },
    { value: 'exploratory_all', label: t('researchViewOptions.exploratoryAll') },
  ]
  const activeTab = query.data ? selectedTab ?? preferredHeadToHeadTab(query.data) : selectedTab ?? initialTab ?? 'direct'

  React.useEffect(() => {
    setSelectedTab(initialTab ?? null)
  }, [initialTab])

  function apply(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setTeam(team.trim())
    setOpponent(opponent.trim())
    if (!initialTab) {
      setSelectedTab(null)
    }
  }

  function updateTab(tab: HeadToHeadTab) {
    setSelectedTab(tab)
    onTabChange?.(tab)
  }

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow={t('eyebrow')}
        title={query.data
          ? t('titleWithTeams', {
              team: formatTeamName(query.data.teams.home.name, locale),
              opponent: formatTeamName(query.data.teams.away.name, locale),
            })
          : t('title')}
        subtitle={t('subtitle')}
      />
      <Card className="p-5" variant="secondary">
        <div className="mb-4">
          <div className="text-sm font-bold">{t('formTitle')}</div>
          <p className="mt-1 text-sm text-muted">{t('formSubtitle')}</p>
        </div>
        <form className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_240px_minmax(180px,auto)]" onSubmit={apply}>
          <SelectInput label={t('teamLabel')} value={team} options={teamOptions} onChange={setTeam} />
          <SelectInput label={t('opponentLabel')} value={opponent} options={teamOptions} onChange={setOpponent} />
          <SelectInput
            label={t('researchViewLabel')}
            value={filters.researchView ?? 'major_tournament'}
            options={researchViewOptions}
            onChange={researchView => setFilters(current => ({ ...current, researchView: researchView as WorldcupResearchQuery['researchView'], offset: 0 }))}
          />
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
            <Button type="submit" className="w-full sm:w-auto" variant="primary">{t('search')}</Button>
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onPress={() => exportMutation.mutate()}
              isDisabled={query.isPending || exportMutation.isPending}
            >
              <Download className="size-4" />
              {t('exportCsv')}
            </Button>
          </div>
        </form>
      </Card>
      {query.isPending && <Card className="p-5 text-sm text-muted" variant="secondary">{t('loading')}</Card>}
      {query.isError && (
        <ResearchStateNotice
          title={isUnauthorizedResearchError(query.error) ? t('accessDeniedTitle') : t('errorTitle')}
          body={isUnauthorizedResearchError(query.error) ? t('accessDeniedBody') : t('error')}
          tone="danger"
        />
      )}
      {query.data && <HeadToHeadResults data={query.data} activeTab={activeTab} onTabChange={updateTab} />}
    </div>
  )
}

function HeadToHeadResults({
  data,
  activeTab,
  onTabChange,
}: {
  data: PublicResearchHeadToHeadResponse
  activeTab: HeadToHeadTab
  onTabChange: (tab: HeadToHeadTab) => void
}) {
  const t = useTranslations('ResearchWorkspace.headToHead')
  const locale = useLocale() as ImplementedLocale
  const homeTeamName = formatTeamName(data.teams.home.name, locale)
  const awayTeamName = formatTeamName(data.teams.away.name, locale)
  const homeWins = data.matches.filter(match => (match.home?.goals ?? -1) > (match.home?.opponentGoals ?? -1)).length
  const awayWins = data.matches.filter(match => (match.away?.goals ?? -1) > (match.away?.opponentGoals ?? -1)).length
  const draws = data.matches.filter(match => match.home && match.home.goals === match.home.opponentGoals).length
  const hasAnyEvidence = data.matches.length > 0 || data.commonOpponents.length > 0

  return (
    <div className="space-y-5">
      {!hasAnyEvidence && (
        <ResearchStateNotice
          title={t('emptyTitle')}
          body={t('emptyWithScope', { team: homeTeamName, opponent: awayTeamName, researchView: data.appliedFilters.researchView ?? 'major_tournament' })}
        />
      )}
      <SourceWarningList title={t('sourceWarningTitle')} warnings={data.quality.warnings} />
      <section className="grid gap-3 md:grid-cols-5">
        <SummaryMetric label={t('summary.matches')} value={data.matches.length} />
        <SummaryMetric label={t('summary.homeWins', { team: homeTeamName })} value={homeWins} />
        <SummaryMetric label={t('summary.awayWins', { opponent: awayTeamName })} value={awayWins} />
        <SummaryMetric label={t('summary.draws')} value={draws} />
        <SummaryMetric label={t('summary.commonOpponents')} value={data.commonOpponents.length} />
      </section>
      <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="overflow-hidden" variant="secondary">
          <Card.Header>
            <div className="w-full space-y-4">
              <div>
                <h2 className="text-lg font-bold">{activeTab === 'direct' ? t('matchesTitle') : t('commonOpponentsTitle')}</h2>
                <p className="mt-1 text-sm text-muted">{activeTab === 'direct' ? t('matchesSubtitle') : t('commonOpponentsSubtitle')}</p>
              </div>
              <div className="flex flex-wrap gap-2" role="tablist" aria-label={t('tabs.label')}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'direct'}
                  className={`rounded-md border px-3 py-2 text-sm font-bold transition ${activeTab === 'direct' ? 'border-primary bg-primary text-primary-foreground' : 'border-divider bg-background text-foreground hover:border-primary/60'}`}
                  onClick={() => onTabChange('direct')}
                >
                  {t('tabs.direct', { count: data.matches.length })}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'common-opponents'}
                  className={`rounded-md border px-3 py-2 text-sm font-bold transition ${activeTab === 'common-opponents' ? 'border-primary bg-primary text-primary-foreground' : 'border-divider bg-background text-foreground hover:border-primary/60'}`}
                  onClick={() => onTabChange('common-opponents')}
                >
                  {t('tabs.commonOpponents', { count: data.commonOpponents.length })}
                </button>
              </div>
            </div>
          </Card.Header>
          <Card.Content className="p-0">
            {activeTab === 'direct' && (
              <>
                {data.matches.length === 0 && <div className="p-5 text-sm text-muted">{t('empty')}</div>}
                {data.matches.map(match => (
                  <HeadToHeadMatchCard key={match.matchId} match={match} />
                ))}
              </>
            )}
            {activeTab === 'common-opponents' && (
              <CommonOpponentGroups data={data} />
            )}
          </Card.Content>
        </Card>
        <HeadToHeadDataNote
          allowedUse={data.allowedUse}
          prohibitedUse={data.prohibitedUse}
          warnings={userVisibleWarnings(data.quality.warnings)}
        />
      </section>
    </div>
  )
}

function commonOpponentCardTitle(row: WorldcupResearchTeamMatchStatRow, locale: ImplementedLocale) {
  const homeName = row.matchHomeTeam ?? row.team
  const awayName = row.matchAwayTeam ?? row.opponent
  return `${formatTeamName(homeName, locale)} vs ${formatTeamName(awayName, locale)}`
}

function commonOpponentScore(row: WorldcupResearchTeamMatchStatRow) {
  if (row.homeGoals !== undefined && row.awayGoals !== undefined) {
    return `${formatValue(row.homeGoals)} - ${formatValue(row.awayGoals)}`
  }
  return `${formatValue(row.goals)} - ${formatValue(row.opponentGoals)}`
}

function CommonOpponentGroups({ data }: { data: PublicResearchHeadToHeadResponse }) {
  const t = useTranslations('ResearchWorkspace.headToHead')
  const locale = useLocale() as ImplementedLocale
  const homeTeamName = formatTeamName(data.teams.home.name, locale)
  const awayTeamName = formatTeamName(data.teams.away.name, locale)

  if (data.commonOpponents.length === 0) {
    return <div className="p-5 text-sm text-muted">{t('commonOpponentsEmpty')}</div>
  }

  return (
    <div className="divide-y divide-divider">
      {data.commonOpponents.map(group => (
        <article key={group.opponent} className="p-4">
          <div className="mb-3">
            <h3 className="text-base font-black">{formatTeamName(group.opponent, locale)}</h3>
            <p className="mt-1 text-sm text-muted">{t('commonOpponentSharedBy', { team: homeTeamName, opponent: awayTeamName })}</p>
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            <CommonOpponentColumn title={homeTeamName} matches={group.home} />
            <CommonOpponentColumn title={awayTeamName} matches={group.away} />
          </div>
        </article>
      ))}
    </div>
  )
}

function CommonOpponentColumn({
  title,
  matches,
}: {
  title: string
  matches: PublicResearchHeadToHeadResponse['commonOpponents'][number]['home']
}) {
  return (
    <div className="min-w-0 rounded-md border border-divider bg-background">
      <div className="border-b border-divider px-3 py-2 text-sm font-black">{title}</div>
      <div className="divide-y divide-divider">
        {matches.map(match => (
          <CommonOpponentMatchCard key={match.matchId} match={match} />
        ))}
      </div>
    </div>
  )
}

function CommonOpponentMatchCard({
  match,
}: {
  match: PublicResearchHeadToHeadResponse['commonOpponents'][number]['home'][number]
}) {
  const t = useTranslations('ResearchWorkspace.headToHead')
  const locale = useLocale() as ImplementedLocale
  const title = commonOpponentCardTitle(match.row, locale)
  const detailLink = matchDetailPath(match.matchId, locale)
  const providerLabels = sourceProviderLabels([match.row])

  return (
    <Link
      to={detailLink.to as any}
      params={detailLink.params as any}
      className="block p-3 transition hover:bg-default-100"
      aria-label={`${title} ${commonOpponentScore(match.row)}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 text-sm font-black">{title}</div>
        <div className="rounded-md bg-primary px-2 py-1 text-sm font-black text-primary-foreground">
          {commonOpponentScore(match.row)}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted">
        <span>
          {formatCompetitionName(match.competitionName, locale)}
          {' '}
          ·
          {' '}
          {match.season}
          {' '}
          ·
          {' '}
          {match.stage ?? t('stageFallback')}
        </span>
        {providerLabels.map(provider => (
          <span key={provider} className="rounded-md border border-divider px-2 py-0.5 font-semibold text-foreground">{provider}</span>
        ))}
      </div>
    </Link>
  )
}

function HeadToHeadDataNote({
  allowedUse,
  prohibitedUse,
  warnings,
}: {
  allowedUse: string[]
  prohibitedUse: string[]
  warnings: string[]
}) {
  const t = useTranslations('ResearchWorkspace.headToHead')
  const [showDetails, setShowDetails] = React.useState(false)

  return (
    <Card className="p-5" variant="secondary">
      <Card.Header>
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <ShieldCheck className="size-4" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-lg font-bold">{t('qualityTitle')}</h2>
            <p className="mt-1 text-sm text-muted">{t('qualityBody')}</p>
          </div>
        </div>
      </Card.Header>
      <Card.Content className="space-y-4">
        <Button size="sm" variant="ghost" onPress={() => setShowDetails(value => !value)}>
          {showDetails ? t('qualityHideDetails') : t('qualityDetails')}
        </Button>
        {showDetails && (
          <div className="space-y-4 border-t border-divider pt-4">
            <EvidenceList title={t('quality.allowedUse')} values={allowedUse} />
            <EvidenceList title={t('quality.prohibitedUse')} values={prohibitedUse} />
            <EvidenceList title={t('quality.warnings')} values={warnings} />
          </div>
        )}
      </Card.Content>
    </Card>
  )
}

function HeadToHeadMatchCard({ match }: { match: PublicResearchHeadToHeadResponse['matches'][number] }) {
  const t = useTranslations('ResearchWorkspace.headToHead')
  const locale = useLocale() as ImplementedLocale
  const home = match.home
  const away = match.away
  const homeName = formatTeamName(home?.team ?? match.home?.team ?? '', locale)
  const awayName = formatTeamName(away?.team ?? match.away?.team ?? '', locale)
  const homeGoals = home?.goals ?? away?.opponentGoals
  const awayGoals = home?.opponentGoals ?? away?.goals
  const scoreText = `${formatValue(homeGoals)} - ${formatValue(awayGoals)}`
  const detailLink = match.matchId ? matchDetailPath(match.matchId, locale) : null
  const providerLabels = sourceProviderLabels([home, away])
  const metrics = [
    { key: 'shotsOnTarget', label: t('metrics.shotsOnTarget'), homeValue: home?.shotsOnTarget, awayValue: away?.shotsOnTarget },
    { key: 'shotsTotal', label: t('metrics.shotsTotal'), homeValue: home?.shotsTotal, awayValue: away?.shotsTotal },
    { key: 'corners', label: t('metrics.corners'), homeValue: home?.corners, awayValue: away?.corners },
    { key: 'expectedGoals', label: t('metrics.expectedGoals'), homeValue: home?.expectedGoals, awayValue: away?.expectedGoals },
  ].filter(metric => (metric.homeValue !== null && metric.homeValue !== undefined) || (metric.awayValue !== null && metric.awayValue !== undefined))

  return (
    <article className="border-t border-divider p-4 first:border-t-0" aria-label={`${homeName} ${scoreText} ${awayName}`}>
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center">
        <div className="text-lg font-black md:text-right">{homeName}</div>
        <div className="justify-self-start rounded-md bg-primary px-4 py-2 text-xl font-black text-primary-foreground md:justify-self-center">
          {scoreText}
        </div>
        <div className="text-lg font-black">{awayName}</div>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2 text-sm text-muted md:justify-center">
        <span>
          {formatCompetitionName(match.competitionName, locale)}
          {' '}
          ·
          {' '}
          {match.season}
          {' '}
          ·
          {' '}
          {match.stage ?? t('stageFallback')}
        </span>
        {detailLink && (
          <Link
            to={detailLink.to as any}
            params={detailLink.params as any}
            className="rounded-md bg-primary px-2 py-1 text-xs font-bold text-primary-foreground"
          >
            {t('openDetail')}
          </Link>
        )}
        {providerLabels.map(provider => (
          <span key={provider} className="rounded-md border border-divider bg-background px-2 py-1 text-xs font-semibold text-foreground">{provider}</span>
        ))}
      </div>
      <div className="mt-4 space-y-3">
        {metrics.map((metric) => {
          const homeNumber = typeof metric.homeValue === 'number' ? metric.homeValue : 0
          const awayNumber = typeof metric.awayValue === 'number' ? metric.awayValue : 0
          const max = Math.max(homeNumber, awayNumber, 1)

          return (
            <div key={metric.key} className="grid grid-cols-[56px_minmax(0,1fr)_92px_minmax(0,1fr)_56px] items-center gap-2 text-sm">
              <div className="text-right font-bold">{formatValue(metric.homeValue)}</div>
              <div className="flex h-2 justify-end rounded-full bg-default-100">
                <div className="h-2 rounded-full bg-primary/80" style={{ width: metricShare(metric.homeValue, max) }} />
              </div>
              <div className="text-center text-xs font-semibold text-muted">{metric.label}</div>
              <div className="h-2 rounded-full bg-default-100">
                <div className="h-2 rounded-full bg-foreground/70" style={{ width: metricShare(metric.awayValue, max) }} />
              </div>
              <div className="font-bold">{formatValue(metric.awayValue)}</div>
            </div>
          )
        })}
      </div>
    </article>
  )
}
